import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  patchUppdrag,
  sendUppdragAttachments,
  UppdragQueryKey,
} from "api/uppdrag.api";
import { useEffect, useState } from "react";
import { shouldShowEmailToWasteStationPing, Uppdrag } from "types/uppdrag.type";
import Alert from "components/Alert";
import Modal from "components/Modal";
import { DocumentationEmailForm } from "components/DocumentationEmailForm";
import { usePreviewPdf } from "hooks/usePreviewPdf";
import PrimaryButton from "components/buttons/PrimaryButton";
import { EnvelopeIcon } from "@heroicons/react/20/solid";
import {
  ContentContainer,
  HeaderContainer,
  ShadowCard,
} from "components/ShadowCard";
import { Headline4 } from "components/typography/Headline4";
import { InspectionPDFPreviewSmall } from "components/FilePreviewSmall";
import { HasSentStatus } from "types/transportDocument.type";
import { SentToCustomerStatusTag } from "components/SentToCustomerStatusTag";
import SecondaryButton from "components/buttons/SecondaryButton";
import { usePatchWasteStation } from "api/wasteStation.api";
import { isEmail } from "helpers/string";
import { useForm } from "react-hook-form";
import { Ping } from "components/Ping";
import { DotMenu } from "components/DotMenu";
import {
  PreliminaryTransportSedelEmailStatus,
  TransportSedel,
  TransportSedelStatus,
} from "types/transportSedel.type";
import { PreliminaryEmailStatusBadge } from "components/PreliminaryEmailStatusBadge";
import { Info } from "components/Info";
import { useNotification } from "hooks/useNotification";
import { Headline6 } from "components/typography/Headline6";

const preliminaryEmailStatusDescription = (ts: TransportSedel): string => {
  const email = ts.wasteStation?.documentationEmail;

  switch (ts.preliminaryEmailStatus) {
    case PreliminaryTransportSedelEmailStatus.Sent:
      return isEmail(email)
        ? `Skickades till ${email} när transporten avgick.`
        : "Skickades när transporten avgick.";
    case PreliminaryTransportSedelEmailStatus.FailedDueToMissingEmailAddress:
      return isEmail(email)
        ? "Kunde inte skickas eftersom epostadress saknades vid avfärd. Den slutliga transportsedeln skickas som vanligt nedan."
        : "Mottagarstationen saknade epostadress vid avfärd. Lägg till en epostadress för att kunna skicka den slutliga transportsedeln.";
    case PreliminaryTransportSedelEmailStatus.FailedForOtherReason:
      return "Något gick fel vid utskicket. Den slutliga transportsedeln kan fortfarande skickas nedan.";
    case PreliminaryTransportSedelEmailStatus.NotSent:
      if (ts.status === TransportSedelStatus.Created) {
        return isEmail(email)
          ? `Skickas automatiskt till ${email} när transporten avgår.`
          : "Skickas automatiskt när transporten avgår. Mottagarstationen saknar epostadress — lägg till en innan avfärd.";
      }
      return "Skickades inte när transporten avgick.";
  }
};

type Props = {
  uppdrag: Uppdrag;
};
export const WasteStation = ({ uppdrag }: Props) => {
  const qc = useQueryClient();
  const transportSedel = uppdrag.transportSedelList.find((x) => true);
  if (!transportSedel) {
    return null;
  }

  /// DocumentationEmail

  const [
    isWasteStationDocumentationModalOpen,
    setIsWasteStationDocumentationModalOpen,
  ] = useState<boolean>(false);

  const {
    register: registerWasteStationDocumentationEmail,
    handleSubmit: handleWasteStationDocumentationEmailSubmit,
    reset: resetWasteStationDocumentationEmail,
    formState: { errors: wasteStationDocumentationEmailErrors },
  } = useForm<{ documentationEmail: string }>();

  const {
    mutate: patchWasteStation,
    isSuccess: isPatchWasteStationSuccess,
    isPending: isPatchingWasteStation,
  } = usePatchWasteStation(transportSedel.wasteStation?.id ?? -1, {
    onSuccess: () => {
      // Det här är för att jag inte orkar kolla vilken av dem det är
      qc.invalidateQueries({
        queryKey: [UppdragQueryKey.GetOne],
      });
      qc.invalidateQueries({
        queryKey: [UppdragQueryKey.GetList],
      });
    },
  });

  useEffect(() => {
    if (isPatchWasteStationSuccess) {
      setIsWasteStationDocumentationModalOpen(false);
    }
  }, [isPatchWasteStationSuccess]);

  const onWasteStationDocumentationEmailSubmit =
    handleWasteStationDocumentationEmailSubmit(
      (data: { documentationEmail: string }) => patchWasteStation(data),
    );

  const { previewPdf, isLoading } = usePreviewPdf();

  const hasValidDocumentationEmail = isEmail(
    transportSedel.wasteStation?.documentationEmail,
  );

  const isReadyToSendForFirstTime =
    (uppdrag.emailToWasteStationStatus === HasSentStatus.NotSent ||
      uppdrag.emailToWasteStationStatus === HasSentStatus.Ignored) &&
    transportSedel.status === TransportSedelStatus.Completed &&
    isEmail(transportSedel.wasteStation?.documentationEmail);

  const isReadyToSendForSecondTime =
    uppdrag.emailToWasteStationStatus === HasSentStatus.Sent &&
    transportSedel.status === TransportSedelStatus.Completed &&
    isEmail(transportSedel.wasteStation?.documentationEmail);

  const hasDocumentationEmailButNotReady =
    !!transportSedel.wasteStation?.documentationEmail &&
    transportSedel.status !== TransportSedelStatus.Completed;

  const notification = useNotification();
  const sendMutation = useMutation({
    ...sendUppdragAttachments(),
    onSuccess: () => {
      notification.info("Transportdokument skickat");
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
    },
  });

  // Bara intressant om stationen vill ha preliminär transportsedel vid avfärd
  const transportSedelListWithPreliminaryStatus =
    uppdrag.transportSedelList.filter(
      (x) => x.wasteStation?.wantsTransportSedelOnDeparture,
    );

  const patchUppdragMutation = useMutation({
    ...patchUppdrag(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });
    },
  });

  return (
    <ShadowCard>
      <HeaderContainer>
        <Headline4 className="flex items-center">
          Till Mottagarstation
          {shouldShowEmailToWasteStationPing(uppdrag) && <Ping />}
          {uppdrag.emailToWasteStationStatus === HasSentStatus.Sent && (
            <SentToCustomerStatusTag
              className="ml-3"
              status={HasSentStatus.Sent}
            />
          )}
        </Headline4>
        {shouldShowEmailToWasteStationPing(uppdrag) && (
          <DotMenu
            items={[
              {
                label: "Ta bort påminnelse",
                onClick: () =>
                  patchUppdragMutation.mutate({
                    id: uppdrag.id,
                    payload: {
                      emailToWasteStationStatus: HasSentStatus.Ignored,
                    },
                  }),
              },
            ]}
          />
        )}
      </HeaderContainer>
      <ContentContainer className="flex flex-col justify-between space-y-8">
        <div className="flex flex-col">
          <Headline6 className="mb-2">Transportsedel</Headline6>
          {uppdrag.transportSedelList.map((x) => (
            <InspectionPDFPreviewSmall
              key={x.id}
              inspectionName="transportsedel.pdf"
              isLoading={isLoading}
              onClick={() => previewPdf(`/transport-sedel/${x.id}/preview-pdf`)}
            />
          ))}
        </div>

        {transportSedelListWithPreliminaryStatus.length > 0 && (
          <div className="flex flex-col">
            <Headline6 className="mb-2">
              Preliminär transportsedel vid avfärd
            </Headline6>
            <div className="flex flex-col gap-y-3">
              {transportSedelListWithPreliminaryStatus.map((x) => (
                <div key={x.id} className="flex flex-col gap-y-1">
                  <div className="flex items-center gap-x-2">
                    {transportSedelListWithPreliminaryStatus.length > 1 && (
                      <span className="text-xs text-gray-500">
                        {x.vehicle?.registrationNumber}
                      </span>
                    )}
                    <PreliminaryEmailStatusBadge transportSedel={x} />
                  </div>
                  <span className="text-xs text-gray-500">
                    {preliminaryEmailStatusDescription(x)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!hasValidDocumentationEmail && (
          <div className="mt-6">
            <Alert
              text="Mottagarstationen saknar Epostadress för rapporter."
              buttonText="Lägg till"
              onButtonClick={() =>
                setIsWasteStationDocumentationModalOpen(true)
              }
            />
          </div>
        )}
        {hasDocumentationEmailButNotReady && (
          <Info
            text={
              "Transportsedeln behöver vägas och signeras innan den kan skickas"
            }
          />
        )}
        {isReadyToSendForFirstTime && (
          <div className="flex flex-col items-center space-y-2">
            <PrimaryButton
              onClick={() =>
                sendMutation.mutate({
                  uppdragId: uppdrag.id,
                  payload: {
                    toWasteStation: true,
                  },
                })
              }
              isLoading={sendMutation.isPending}
              loadingText="Skickar..."
              className="w-full truncate"
            >
              <EnvelopeIcon className="mr-2 h-5 w-5" />
              {`Skicka till ${transportSedel.wasteStation?.name}`}
              {/* {getSendButtonText(existingFiles, includeTransportDocument)} */}
            </PrimaryButton>
            <span className="text-xs text-gray-500">
              Skickas till
              <span className="ml-1 font-medium text-gray-900">
                {transportSedel.wasteStation?.documentationEmail}
              </span>
            </span>
          </div>
        )}

        {isReadyToSendForSecondTime && (
          <div className="flex flex-col items-center space-y-2">
            <SecondaryButton
              onClick={() =>
                sendMutation.mutate({
                  uppdragId: uppdrag.id,
                  payload: {
                    toWasteStation: true,
                  },
                })
              }
              isLoading={sendMutation.isPending}
              loadingText="Skickar..."
              className="w-full"
            >
              <EnvelopeIcon className="mr-2 h-5 w-5" />
              Skicka igen
              {/* {getSendButtonText(existingFiles, includeTransportDocument)} */}
            </SecondaryButton>
            <span className="text-xs text-gray-500">
              Skickas till
              <span className="ml-1 font-medium text-gray-900">
                {transportSedel.wasteStation?.documentationEmail}
              </span>
            </span>
          </div>
        )}

        <Modal
          isOpen={isWasteStationDocumentationModalOpen}
          onClose={() => setIsWasteStationDocumentationModalOpen(false)}
          afterClose={() =>
            resetWasteStationDocumentationEmail({
              documentationEmail:
                transportSedel.wasteStation?.documentationEmail,
            })
          }
          title="Ändra Mottagarstation"
          actionText="Spara"
          actionHandler={onWasteStationDocumentationEmailSubmit}
          isLoading={isPatchingWasteStation}
          loadingText="Sparar..."
        >
          <DocumentationEmailForm
            register={registerWasteStationDocumentationEmail}
            errors={wasteStationDocumentationEmailErrors}
            wasteStationName={transportSedel.wasteStation?.name ?? ""}
          />
        </Modal>

        {/* {uppdrag && (
               <div className="flex flex-col items-center space-y-2">
                 <PrimaryButton
                   onClick={() =>
                     sendMutation.mutate({
                       uppdragId: uppdrag.id,
                       payload: {
                         toCustomer: true,
                       },
                     })
                   }
                   isLoading={sendMutation.isPending}
                   loadingText="Skickar..."
                   className="w-full"
                 >
                   <EnvelopeIcon className="mr-2 h-5 w-5" />
                   Skicka till kund
                 </PrimaryButton>
                 <span className="text-sm text-gray-500">
                   Skickas till
                   <span className="ml-1 font-medium text-gray-900">
                     {getDocumentationEmail(uppdrag.facility)}
                   </span>
                 </span>
               </div>
             )} */}
      </ContentContainer>
    </ShadowCard>
  );
};
